import { Info, Lightbulb, AlertTriangle } from "lucide-react";
import type { ArticleBlock } from "@/data/articles";

/**
 * ArticleRenderer — renders a typed article body to semantic HTML.
 * Uses Tailwind typography conventions but avoids @tailwindcss/typography
 * so colors stay on the design-system tokens.
 */
const calloutStyles: Record<
  "tip" | "warn" | "info",
  { wrap: string; iconColor: string; Icon: typeof Info }
> = {
  tip: {
    wrap: "bg-primary/10 border-primary/30",
    iconColor: "text-primary",
    Icon: Lightbulb,
  },
  warn: {
    wrap: "bg-destructive/10 border-destructive/30",
    iconColor: "text-destructive",
    Icon: AlertTriangle,
  },
  info: {
    wrap: "bg-muted border-border",
    iconColor: "text-foreground",
    Icon: Info,
  },
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);

const ArticleRenderer = ({ blocks }: { blocks: ArticleBlock[] }) => {
  return (
    <div className="min-w-0 max-w-full space-y-6 break-words">
      {blocks.map((b, i) => {
        switch (b.type) {
          case "p":
            return (
              <p key={i} className="text-foreground leading-relaxed text-base sm:text-lg">
                {b.text}
              </p>
            );
          case "h2":
            return (
              <h2
                key={i}
                id={b.id ?? slugify(b.text)}
                className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-2 scroll-mt-24"
              >
                {b.text}
              </h2>
            );
          case "h3":
            return (
              <h3 key={i} className="text-xl font-bold text-foreground mt-8 mb-1">
                {b.text}
              </h3>
            );
          case "ul":
            return (
              <ul key={i} className="list-disc pl-6 space-y-2 text-foreground text-base sm:text-lg">
                {b.items.map((it, j) => (
                  <li key={j} className="leading-relaxed">
                    {it}
                  </li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={i} className="list-decimal pl-6 space-y-2 text-foreground text-base sm:text-lg">
                {b.items.map((it, j) => (
                  <li key={j} className="leading-relaxed">
                    {it}
                  </li>
                ))}
              </ol>
            );
          case "callout": {
            const { wrap, iconColor, Icon } = calloutStyles[b.tone];
            return (
              <aside
                key={i}
                className={`border-l-4 rounded-r-lg p-4 sm:p-5 flex gap-3 ${wrap}`}
              >
                <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
                <div>
                  {b.title && (
                    <p className="font-semibold text-foreground mb-1">{b.title}</p>
                  )}
                  <p className="text-foreground/90 leading-relaxed">{b.text}</p>
                </div>
              </aside>
            );
          }
          case "quote":
            return (
              <blockquote
                key={i}
                className="border-l-4 border-primary pl-5 italic text-muted-foreground my-6"
              >
                "{b.text}"
                {b.cite && <footer className="text-sm not-italic mt-2">— {b.cite}</footer>}
              </blockquote>
            );
          case "table":
            return (
              <div key={i} className="overflow-x-auto my-4 rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-offwhite">
                    <tr>
                      {b.headers.map((h, j) => (
                        <th
                          key={j}
                          className="text-left font-semibold text-foreground px-4 py-3 border-b border-border"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, ri) => (
                      <tr key={ri} className="border-b border-border last:border-0 hover:bg-offwhite/50">
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-4 py-3 text-foreground align-top">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "image":
            return (
              <figure key={i} className="my-6">
                <img
                  src={b.src}
                  alt={b.alt}
                  loading="lazy"
                  className="w-full rounded-lg shadow-xs"
                />
                {b.caption && (
                  <figcaption className="text-sm text-muted-foreground mt-2 text-center italic">
                    {b.caption}
                  </figcaption>
                )}
              </figure>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default ArticleRenderer;
